// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving assets with comprehensive filtering capabilities.
 * Provides functionality to fetch assets based on various criteria including
 * ownership, location, status, and text search. Includes client-side filtering
 * for text searches.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { paginatedResultSchema } from '@/api/paging';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { detailedAssetSchema } from '@/asset/api/types';
import { AssetAssignmentStatus } from '@/constants/asset-assignment-status';
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { AssetInventoryStatus } from '@/constants/asset-inventory-status';
import { AssetTransitStatus } from '@/constants/asset-transit-status';
import { LocationCompanyType } from '@/constants/location-company-type';
import { Sort } from '@/constants/sort';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { omit } from 'lodash-es';
import queryString from 'query-string';

/** Reads a sort saved before the item sort was renamed from product. */
function toItemSortProperty(property: unknown): unknown {
  return property === 'product' ? 'item' : property;
}

/**
 * Schema defining the valid properties for sorting assets.
 * Includes common fields like serial number, project details,
 * and location information.
 */
export const assetSortPropertySchema = z.preprocess(
  toItemSortProperty,
  z.enum(['serial', 'project', 'item', 'location', 'owner', 'activity'])
);

export type AssetSortProperty = z.infer<typeof assetSortPropertySchema>;

/**
 * Schema for validating the API response when fetching assets.
 * Transforms null data arrays into empty arrays for consistent handling.
 */
const assetsResponseSchema = z
  .object({
    data: paginatedResultSchema(detailedAssetSchema),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    assets: data.results,
    totalCount: data.totalCount,
  }));

type AssetsResponse = z.infer<typeof assetsResponseSchema>;

/**
 * Defines the possible ownership states of an asset.
 */
type AssetOwnershipStatus = 'manufacturer' | 'customer';

/**
 * Comprehensive filter options for querying assets.
 * @property {string | undefined} query - Optional search query for filtering assets
 * @property {boolean | 'all' | undefined} scrapped - Optional filter for assets that have been scrapped
 * @property {AssetOwnershipStatus[] | undefined} ownership - Optional filter for assets by ownership status
 * @property {AssetAssignmentStatus[] | undefined} assignment - Optional filter for assets by assignment status
 * @property {string[] | undefined} productIds - Optional filter for assets by product type
 * @property {string[] | undefined} customerIds - Optional filter for assets by customer
 * @property {string[] | undefined} locationIds - Optional filter for assets by location
 * @property {AssetTransitStatus[] | undefined} transitStatus - Optional filter for assets by transit status
 * @property {AssetInventoryStatus[] | undefined} inventoryStatus - Optional filter for assets by inventory status
 * @property {string[] | undefined} customerLocations - Optional filter for assets by customer location
 * @property {AssetFunctionalStatus[] | undefined} functionalStatuses - Optional filter for assets by functional status
 * @property {LocationCompanyType[] | undefined} locationCompanyTypes - Optional filter for assets by location company type
 */
export interface AssetFilter {
  pageSize: number;
  pageNumber?: number;
  query?: string;
  scrapped?: boolean | 'all';
  ownership?: AssetOwnershipStatus[];
  assignment?: AssetAssignmentStatus[];
  productIds?: string[];
  customerIds?: string[];
  locationIds?: string[];
  transitStatus?: AssetTransitStatus[];
  inventoryStatus?: AssetInventoryStatus[];
  customerLocations?: string[];
  functionalStatuses?: AssetFunctionalStatus[];
  locationCompanyTypes?: LocationCompanyType[];
  // Deep-linked from the accounting pages; there is no visible control for it.
  currency?: IsoCurrencyCode;
  sort?: Sort<AssetSortProperty>;
}

export function serializeAssetFilter(filter?: AssetFilter): string {
  if (!filter) {
    return '';
  }
  return queryString.stringify({
    ...omit(filter, 'sort'),
    sortBy: filter.sort?.property,
    sortOrder: filter.sort?.direction ?? 'asc',
  });
}

/**
 * Retrieves a filtered collection of assets from the API.
 * Handles both server-side filtering via query parameters and client-side
 * text searching. The function transforms the API response and applies
 * additional filtering based on the text search query if provided.
 *
 * @param filter - Optional filter criteria for the asset query
 * @returns Filtered list of assets and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getAssets(filter?: AssetFilter): Promise<AssetsResponse> {
  const url = '/v1/asset';
  const response = await api.get(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      limit: filter?.pageSize,
      page: filter?.pageNumber,
      searchQuery: filter?.query,
      scrapped: filter?.scrapped,
      atSite: filter?.locationIds,
      atCustomerSites: filter?.customerLocations,
      forCustomer: filter?.customerIds,
      forItem: filter?.productIds,
      withFunctionalStatuses: filter?.functionalStatuses,
      withOwner: filter?.ownership,
      withProjectStatus: filter?.assignment,
      withTransitStatuses: filter?.transitStatus,
      withInventoryStatuses: filter?.inventoryStatus,
      withLocationCompanyType: filter?.locationCompanyTypes,
      currency: filter?.currency,
      sortBy: filter?.sort?.property,
      sortOrder: filter?.sort?.direction ?? 'asc',
    },
  });

  return parseResponse(assetsResponseSchema, response.data, {
    schemaName: 'Assets',
    endpoint: url,
  });
}
